import { appLocaleSchema, type AppLocale } from "@metaadsmanager/tk-master-contracts";

export const localeStorageKey = "tk-master.locale";
/** 产品默认语言；静态 HTML 与 hydration 首帧共用，客户端偏好在 LocaleProvider 挂载后套用。 */
export const defaultAppLocale: AppLocale = "zh-CN";
let runtimeLocale: AppLocale | null = null;

/** 浏览器拒绝持久化访问时，将语言偏好降级为当前页面内状态。 */
export function readLocalePreference(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(localeStorageKey) ?? runtimeLocale;
  } catch {
    return runtimeLocale;
  }
}

export function writeLocalePreference(locale: AppLocale): void {
  try {
    window.localStorage.setItem(localeStorageKey, locale);
    runtimeLocale = null;
  } catch {
    runtimeLocale = locale;
    // 存储能力不是界面切换或请求本地化的前置条件。
  }
}

const browserLocale = (languages: readonly string[]): AppLocale => {
  for (const language of languages) {
    const normalized = language.toLowerCase();
    if (normalized === "zh" || normalized.startsWith("zh-")) return "zh-CN";
    if (normalized === "en" || normalized.startsWith("en-")) return "en-US";
  }
  return defaultAppLocale;
};

/** 使用设备级偏好解析当前语言；未知语言回退产品默认（简体中文）。 */
export function resolveInitialLocale(
  storedLocale: string | null,
  languages: readonly string[],
): AppLocale {
  const stored = appLocaleSchema.safeParse(storedLocale);
  return stored.success ? stored.data : browserLocale(languages);
}

/** 浏览器请求可随时读取的当前语言，不缓存过期偏好。 */
export function getCurrentLocale(): AppLocale {
  if (typeof window === "undefined") return defaultAppLocale;
  return resolveInitialLocale(
    readLocalePreference(),
    navigator.languages.length > 0 ? navigator.languages : [navigator.language],
  );
}

/**
 * useSyncExternalStore 快照：客户端优先读 bootstrap / setLocale 写入的 `data-locale`。
 * 写入方必须先更新该属性再 notify，否则订阅者读到旧值且 React 会跳过重渲染。
 */
export function getLocaleSnapshot(): AppLocale {
  if (typeof window === "undefined") return defaultAppLocale;
  const fromDom = document.documentElement.getAttribute("data-locale");
  if (fromDom === "en-US" || fromDom === "zh-CN") return fromDom;
  return getCurrentLocale();
}

/**
 * 阻塞脚本：在静态 HTML 绘制前写入 `html.lang` / `data-locale`。
 * React 仍从 defaultAppLocale hydration，再由 LocaleProvider 同步 state。
 */
export function buildLocaleBootstrapScript(): string {
  const key = JSON.stringify(localeStorageKey);
  const fallback = JSON.stringify(defaultAppLocale);
  return `(function(){try{var k=${key},d=${fallback},s=null;try{s=localStorage.getItem(k)}catch(e){}var ok=s==="zh-CN"||s==="en-US";var l=ok?s:null;if(!l){var langs=navigator.languages&&navigator.languages.length?navigator.languages:[navigator.language];for(var i=0;i<langs.length;i++){var n=String(langs[i]||"").toLowerCase();if(n==="zh"||n.indexOf("zh-")===0){l="zh-CN";break}if(n==="en"||n.indexOf("en-")===0){l="en-US";break}}if(!l)l=d}var el=document.documentElement;el.lang=l;el.setAttribute("data-locale",l)}catch(e){}})();`;
}

/** 日期时间始终使用当前浏览器时区；无 Intl 时仅回退 UTC。 */
export function getBrowserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

type MessageNode = string | { readonly [key: string]: MessageNode };

/** 为词典一致性测试展平并排序所有叶子键，同时拒绝空文案。 */
export function flattenMessageKeys(
  input: { readonly [key: string]: MessageNode },
  prefix = "",
): string[] {
  const keys: string[] = [];
  for (const [key, value] of Object.entries(input)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (typeof value === "string") {
      if (!value.trim()) throw new Error(`Empty i18n message: ${path}`);
      keys.push(path);
    } else {
      keys.push(...flattenMessageKeys(value, path));
    }
  }
  return keys.sort();
}
